import * as React from "react";
import "./EqPublicEventBoard.scss";
import { Row, Col, Skeleton } from "antd";
import EqButton from "../../../components/custom/eqButton/EqButton";
import {
    ApiEndpoints,
    GetEventMessage,
    ValidateIsTrainer,
    ValidateIsSchool,
    openNotificationWithIcon,
} from "../../../components/shared/Common";
import Api from "../../../components/shared/Api";
import { useTranslation } from "react-i18next";
import { IEqUiProfile } from "../../../components/shared/interfaces/IEqEventDto";
import { useAuthState } from "../../../components/context/Context";
import { Drawer, Spin } from "antd-v5";
import { eqNotificationTypes } from "../../../components/shared/enums";
import EqPublicEventCard from "../../eqPublicEventCard/EqPublicEventCard";
import { useNavigate } from "react-router-dom";
import EqCreateOrEditEvent from "../eqCreateorEditEvent/EqCreateOrEditEvent";
import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { IEqUiSelectModel } from "../../../components/shared/interfaces/ILocationDto";
import {  EqUiPaymentAccount } from "../../../components/shared/interfaces/ridingCourse/IRidingCourse";
import { EqUiStudentGroup } from "../../../components/shared/interfaces/IUserGroupDto";
import { GetSchoolStudentsDto } from "../../../components/shared/interfaces/schoolTraining/school";
import { IEqPublicEvent } from "../../../components/shared/interfaces/GetSchoolTrainingInfoDto";
import { EqImageInfoDto } from "../../../components/shared/interfaces/Images/IImage";

interface IEqPublicEventBoardProps {
    updateCount?: (count: number) => void;
   
}

interface EventRefType {
    [key: string]: HTMLDivElement | null;
}

interface ApiResponse<T> {
    status: number;
    data: T;
}

export interface ImageUploadResponse extends EqImageInfoDto {
    id: string;
    url: string;
}

const EqPublicEventBoard = ({ updateCount }: IEqPublicEventBoardProps) => {
    const userDetails = useAuthState();
    const isTrainer: boolean = ValidateIsTrainer(userDetails);
    const isSchool: boolean = ValidateIsSchool(userDetails);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [creatorUser, setCreatorUser] = React.useState<IEqUiProfile>();
    const [stripeAccountInfo, setStripeAccountInfo] = React.useState<EqUiPaymentAccount>();
    const [spinning, setSpinning] = React.useState<boolean>(false);

    const [message, setMessage] = React.useState<string>("");
    const [isError, setIsError] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");

    const [areas, setAreas] = React.useState<IEqUiSelectModel[]>([]);
   
    const [imagesData, setImagesData] = React.useState<EqImageInfoDto[]>([]);

    const [CreatePublicEventShow, setCreatePublicEventShow] = React.useState<boolean>(false);
    const [highlightedEventId, setHighlightedEventId] = useState<string | null>(null);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const EventId = queryParams.get('id');

    const [eventsList, setEventsList] = React.useState<IEqPublicEvent[] | null>(null);
    const [editEvent, setEditEvent] = React.useState<IEqPublicEvent | null>(null);
    const [showEditDrawer, setShowEditDrawer] = React.useState<boolean>(false);

    const EventRef = useRef<EventRefType>({});

    React.useEffect(() => {
        getImages();
        getAreas();
        GetUserPublicEvents();
        GetStripeAccount();
    }, []);

    const GetStripeAccount = () => {
        Api.get<EqUiPaymentAccount>(ApiEndpoints.StripeConnectAccount.Get).then(
            (response: ApiResponse<EqUiPaymentAccount>) => {
                if (response.status === 200) {
                    setStripeAccountInfo(response.data);
                }
            },
            (error) => {
                console.log(error);
            }
        );
    };

    const GetUserPublicEvents = async () => {
        let path = ApiEndpoints.EqOwnerPublicEvent.GetAll;
        Api.get<IEqPublicEvent[]>(path).then(
            (response: ApiResponse<IEqPublicEvent[]>) => {
                if (response && response.status === 200) {
                    const data = response.data ? [...response.data] : new Array<IEqPublicEvent>();

                    const sortedEvents = data.sort((a, b) => {
                        if (a.isDraft && !b.isDraft) return -1;
                        if (!a.isDraft && b.isDraft) return 1;
                        return b.createdDate - a.createdDate;
                    });

                    setEventsList(sortedEvents);
                } else {
                    setIsError(true);
                    setErrorMessage(GetEventMessage(response.status));
                }
            },
            (error) => {
                setIsError(true);
                setErrorMessage(GetEventMessage(error.response.status));
            }
        );
    };

    const handleCreateClick = () => {
        setCreatePublicEventShow(true);
    };

    const GetUserProfile = () => {
        Api.get<IEqUiProfile>(ApiEndpoints.GetTrainerProfile).then(
            (response: ApiResponse<IEqUiProfile>) => {
                if (response && response.status === 200) {
                    setIsError(false);
                    setMessage("");
                    setCreatorUser(response.data);
                } else {
                    setIsError(true);
                    setMessage(GetEventMessage(response.status));
                }
            },
            (error) => {
                setIsError(true);
                setMessage(GetEventMessage(error.response.status));
            }
        );
    };

    const removeItem = (eventId: string) => {
        if (eventsList) {
            const updatedList = eventsList.filter((item: IEqPublicEvent) => item.id !== eventId);
            setEventsList(updatedList);
        }
    };

    const handleOnCreateEvent = async (data: IEqPublicEvent) => {
        try {
            setSpinning(true);
            const response = await Api.post<IEqPublicEvent>(ApiEndpoints.EqOwnerPublicEvent.Create, data);

            if (response && response.status === 200) {
                const newEvent = response.data;
                const events = eventsList ? [...eventsList] : [];

                if (newEvent.isDraft) {
                    openNotificationWithIcon(
                        t("saved"),
                        t("draftSaved"),
                        eqNotificationTypes.success
                    );

                    const draftIndex = events.findIndex((event) => event.isDraft);
                    if (draftIndex !== -1) {
                        events[draftIndex] = newEvent;
                    } else {
                        events.unshift(newEvent);
                    }
                } else {
                    events.unshift(newEvent);
                }

                setEventsList(events);

                if (!newEvent.isDraft) {
                    navigate(`/eventSuccess`, {
                        state: {
                            eventId: newEvent.id,
                            eventTitle: newEvent.title,
                        },
                    });
                }

                if (updateCount) {
                    updateCount(events.length);
                }

                setCreatePublicEventShow(false);
            } else {
                openNotificationWithIcon(t("serverError"), t("generalServerError"));
            }
        } catch (error) {
            openNotificationWithIcon(t("serverError"), t("generalServerError"));
            console.error("Error creating or updating event:", error);
        } finally {
            setSpinning(false);
        }
    };

    const handleOnUpdateEvent = async (data: IEqPublicEvent) => {
        try {
            setSpinning(true);
            const response = await Api.put<IEqPublicEvent>(ApiEndpoints.EqOwnerPublicEvent.Update, data);
            if (response && response.status === 200) {
                const updatedEvent = response.data;
              
                // Find the event in the list
                const events = eventsList ? [...eventsList] : [];

                const foundIndex = events.findIndex((x) => x.id === updatedEvent.id);

                if (foundIndex !== -1) {
                    const prevEvent = events[foundIndex];

                    // Check draft status before and after update to show appropriate notification
                    if (prevEvent.isDraft && !updatedEvent.isDraft) {
                        openNotificationWithIcon(
                            t("created"),
                            t("eventCreated"),
                            eqNotificationTypes.success
                        );
                    } else if (updatedEvent.isDraft) {
                        openNotificationWithIcon(
                            t("updated"),
                            t("draftUpdated"),
                            eqNotificationTypes.success
                        );
                    } else {
                        openNotificationWithIcon(
                            t("updated"),
                            t("eventUpdated"),
                            eqNotificationTypes.success
                        );
                    }

                    // Update the event in the list
                    UpdateEventList(updatedEvent);

                    if (prevEvent.isDraft && !updatedEvent.isDraft) {
                        // Navigate to /eventSuccess with event ID and title as query params
                        navigate(`/eventSuccess`, {
                            state: {
                                eventId: updatedEvent.id,
                                eventTitle: updatedEvent.title,
                            },
                        });
                    }
                }
            } else {
                openNotificationWithIcon(
                    t("serverError"),
                    t("generalServerError"),
                    eqNotificationTypes.error
                );
            }
        } catch (error) {
            console.error("Error creating or updating event:", error);
            openNotificationWithIcon(
                t("serverError"),
                t("generalServerError"),
                eqNotificationTypes.error
            );
        } finally {
            setSpinning(false);
        }
    };

    const UpdateEventList = (updatedEvent: IEqPublicEvent) => {
        if (eventsList) {
            const updatedEvents = eventsList.map((event: IEqPublicEvent) =>
                event.id === updatedEvent.id ? updatedEvent : event
            );
            setEventsList(updatedEvents);
        }

        if (editEvent?.id === updatedEvent.id) {
            setEditEvent(updatedEvent);
        }

        if (updateCount && eventsList) {
            updateCount(eventsList.length);
        }
    };

    const DeleteEvent = (eventId: string) => {
        Api.delete(`${ApiEndpoints.EqOwnerPublicEvent.Delete}/${eventId}`).then(
            (response) => {
                if (response && response.status === 200) {
                    openNotificationWithIcon(
                        t("deleted"),
                        t("eventDeleted"),
                        eqNotificationTypes.success
                    );
                    removeItem(eventId);
                } else {
                    setIsError(true);
                }
            },
            (error) => {
                if (error?.response?.status === 412) {
                    openNotificationWithIcon(
                        t("Conflict"),
                        t("Cannot delete the event as it has associated class orders.")
                    );
                } else {
                    openNotificationWithIcon(t("serverError"), t("generalServerError"));
                }
                setIsError(true);
            }
        );
    };

    const RemoveAttendeeClassOrder = (orderId: string, refund: boolean) => {
        Api.delete(
            `${ApiEndpoints.EqOwnerPublicEvent.RemoveAttendeeClassOrder}/${orderId}?refund=${refund}`
        )
            .then((response) => {
                if (response && response.status === 200) {
                    openNotificationWithIcon(
                        t("removed"),
                        t("attendeeRemoved"),
                        eqNotificationTypes.success
                    );
                    UpdateEventList(response.data);
                } else {
                    setIsError(true);
                }
            })
            .catch((error) => {
                setIsError(true);
                openNotificationWithIcon(t("serverError"), t("generalServerError"));
            });
    };

    const RemoveAttendeeClassOrderWithAmount = (
        orderId: string,
        refund: boolean,
        refundAmount?: number
    ) => {
        setSpinning(true);
        Api.delete(
            `${ApiEndpoints.EqOwnerPublicEvent.RemoveAttendeeClassOrderWithAmount}/${orderId}?refund=${refund}&refundAmount=${refundAmount}`
        )
            .then((response) => {
                if (response && response.status === 200) {
                    const message = refund
                        ? t("attendeeRemovedAndRefunded")
                        : t("attendeeRemoved");

                    openNotificationWithIcon(
                        t("removed"),
                        message,
                        eqNotificationTypes.success
                    );
                    UpdateEventList(response.data);
                } else {
                    setIsError(true);
                }
                setSpinning(false);
            })
            .catch((error) => {
                setIsError(true);
                openNotificationWithIcon(t("serverError"), t("generalServerError"));
                setSpinning(false);
            });
    };

    const getAreas = () => {
        Api.get<IEqUiSelectModel[]>(ApiEndpoints.Location.GetUserLocationsOptions).then(
            (response: ApiResponse<IEqUiSelectModel[]>) => {
                if (response && response.status === 200) {
                    setIsError(false);
                    setMessage("");
                    setAreas(response.data);
                } else {
                    setIsError(true);
                    setMessage(GetEventMessage(response.status));
                }
            },
            (error) => {
                setIsError(true);
                setMessage(GetEventMessage(error.response.status));
            }
        );
    };


    const updateImageLibrary = (newImage: EqImageInfoDto) => {
        setImagesData((prevImages: EqImageInfoDto[]) => [...prevImages, newImage]);
    };

    const removeFromImageLibrary = (imageId: string) => {
        setImagesData((prevImages: EqImageInfoDto[]) => 
            prevImages.filter((image: EqImageInfoDto) => image.id !== imageId)
        );
    };

    const getImages = () => {
        Api.get<EqImageInfoDto[]>(ApiEndpoints.Image.GetUserImages).then(
            (response: ApiResponse<EqImageInfoDto[]>) => {
                if (response.data && response.data.length) {
                    setImagesData(response.data);
                }
            },
            () => {
                setIsError(true);
                setMessage("Error Occurred while fetching images from server");
            }
        );
    };

    React.useEffect(() => {
        if (EventId && eventsList && eventsList?.length > 0) {
            setHighlightedEventId(EventId);
            const timeoutId = setTimeout(() => {
                const EventElement = EventRef.current[EventId];
                if (EventElement) {
                    EventElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    EventElement.style.border = "2px solid #FF8838";
                    EventElement.style.transition = "border 0.5s ease";
                    setTimeout(() => {
                        EventElement.style.border = "none";
                        setHighlightedEventId(null);
                    }, 3000);
                }
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [EventId, eventsList]);

    const handleEditEvent = (eventId: string) => {
        const selectedEvent = eventsList?.find((event: IEqPublicEvent) => event.id === eventId);
        if (selectedEvent) {
            setEditEvent(selectedEvent);
            setShowEditDrawer(true);
        } else {
            console.error("Event not found");
        }
    };

    const handleCloseEditDrawer = () => {
        setEditEvent(null);
        setShowEditDrawer(false);
    };

    return (
        <div className={`eq-public-event-board`}>
            <div className="">
                <Spin spinning={spinning} size="large">
                    <Row align="middle" justify="end" style={{ margin: "10px 0" }}>
                        {!CreatePublicEventShow && (isTrainer || isSchool) && (
                            <Row align="middle" justify="end">
                                <Col className="eq-event-buttons">
                                    <EqButton
                                        classNames="btn-background-orange"
                                        onClick={handleCreateClick}
                                    >
                                        {t("createEvent")}
                                    </EqButton>
                                </Col>
                            </Row>
                        )}
                    </Row>

                    <Row className="eq-event-heading-line"></Row>

                    <Row>
                        {[...Array(5)].map((_, index) => (
                            <Skeleton
                                key={index}
                                loading={!eventsList}
                                paragraph={{ rows: 2 }}
                                active
                            />
                        ))}
                    </Row>
                    {eventsList && eventsList.length > 0 && (
                        <Row gutter={[16, 16]} className="event-card-list">
                            {eventsList.map((eventData: IEqPublicEvent) => (
                                <Col xs={24} sm={12} xl={8} xxl={6} key={eventData.id}
                                    ref={(el) => (EventRef.current[eventData.id as string] = el)}>
                                    <EqPublicEventCard
                                        eventData={eventData}
                                        onEdit={() => handleEditEvent(eventData.id as string)}
                                        onDelete={() => DeleteEvent(eventData.id as string)}
                                        onCardClick={() => navigate(`/publicEventSingle?id=${eventData.id}`)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}

                    {CreatePublicEventShow && (
                        <Drawer
                            closable={false}
                            width={1000}
                            open={CreatePublicEventShow}
                            children={
                                <Spin spinning={spinning} size="large">
                                    <EqCreateOrEditEvent
                                        imagesData={imagesData}
                                        creatorUser={creatorUser}
                                        stripeAccountInfo={stripeAccountInfo}
                                        areas={areas}
                                        onCreateEvent={handleOnCreateEvent}
                                        onCloseEvent={() => setCreatePublicEventShow(false)}
                                        onImageUploadSuccess={updateImageLibrary}
                                        onImageDeleteSuccess={removeFromImageLibrary}
                                    />
                                </Spin>
                            }
                        />
                    )}

                    <Drawer
                        closable={false}
                        width={1000}
                        open={showEditDrawer}
                        children={
                            <Spin spinning={spinning} size="large">
                                <EqCreateOrEditEvent
                                    imagesData={imagesData}
                                    creatorUser={creatorUser}
                                    stripeAccountInfo={stripeAccountInfo}
                                    areas={areas}
                                    eventData={editEvent?editEvent:undefined}
                                    onEditEvent={handleOnUpdateEvent}
                                    onRemoveClassOrder={RemoveAttendeeClassOrderWithAmount}
                                    onCloseEvent={handleCloseEditDrawer}
                                    onImageUploadSuccess={updateImageLibrary}
                                    onImageDeleteSuccess={removeFromImageLibrary}
                                />
                            </Spin>
                        }
                    />
                </Spin>
            </div>
        </div>
    );
};

export default EqPublicEventBoard;